import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ArrowLeft, CheckCircle2, Download, ExternalLink, FileText } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { useAllProfiles } from "@/hooks/use-admin-data";
import { useTeamEntries, type TeamEntry } from "@/hooks/use-team-entries";
import { todayIstIso } from "@/lib/tz";
import { modCanWorkspace, type ModeratorPermissions } from "@/lib/moderator-permissions";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const searchSchema = z.object({ date: z.string().optional() });

export const Route = createFileRoute("/_app/today-tasks_/$id")({
  validateSearch: (s) => searchSchema.parse(s),
  component: TodayTaskSummaryPage,
  head: () => ({ meta: [{ title: "Today's Task Summary" }] }),
});

const NO_HOURS_DEPTS: Department[] = ["editor", "graphic_designer", "ai", "scriptwriter"];

const ROLE_LABELS: Partial<Record<Department, string>> = {
  editor: "Content Editor",
  graphic_designer: "Designer",
  ai: "AI",
  scriptwriter: "Scriptwriter",
  marketer: "Marketer",
  seo: "SEO Executive",
  smm: "SMM Executive",
  calling: "Calling Agent",
  hr: "HR Executive",
  admin: "Admin",
};

type UiStatus = "completed" | "in_progress" | "pending" | "no_update";

const STATUS_PILL: Record<UiStatus, { bg: string; fg: string; label: string }> = {
  completed: { bg: "#DCFCE7", fg: "#16A34A", label: "Completed" },
  in_progress: { bg: "#EDE9FE", fg: "#7C3AED", label: "In Progress" },
  pending: { bg: "#FEF3C7", fg: "#B45309", label: "Pending" },
  no_update: { bg: "#F3F4F6", fg: "#6B7280", label: "No Update" },
};

const NO_STATUS_TABLES = ["calling_submissions", "calling_other_tasks"];

function classifyEntryStatus(e: TeamEntry): UiStatus {
  const raw = (e.details as Record<string, unknown> | undefined)?.raw as
    | Record<string, unknown>
    | undefined;
  const table = String(
    e.source_table ?? (e.details as Record<string, unknown> | undefined)?.source_table ?? "",
  );
  if (NO_STATUS_TABLES.includes(table)) return "completed";
  const s = String(raw?.status ?? "").toLowerCase();
  if (s === "completed" || s === "done" || e.status === "approved") return "completed";
  if (s === "work_in_progress" || s === "in_progress") return "in_progress";
  if (s === "") return "completed";
  return "pending";
}

function parseHHMM(t: unknown): number | null {
  if (!t || typeof t !== "string") return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function fmt12(hhmm: unknown): string | null {
  if (typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12).padStart(2, "0")}:${m[2]} ${ampm}`;
}

function fmtHM(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtDateLong(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function fmtTs(ts: string | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Review = {
  id: string;
  employee_id: string;
  review_date: string;
  remark: string | null;
  remark_by: string | null;
  remark_at: string | null;
  checked_by: string | null;
  checked_at: string | null;
};

function TodayTaskSummaryPage() {
  const { id } = Route.useParams();
  const { date: qDate } = Route.useSearch();
  const date = qDate || todayIstIso();

  const { profile, role, loading } = useAuth();
  const { data: profiles = [] } = useAllProfiles();
  const { data: entries = [], isLoading } = useTeamEntries({ date });
  const qc = useQueryClient();

  const employee = profiles.find((p) => p.id === id) ?? null;

  const dayEntries = useMemo(
    () =>
      entries
        .filter((e) => e.user_id === id && e.submission_date === date)
        .sort((a, b) => {
          const ra = (a.details as Record<string, unknown> | undefined)?.raw as
            | Record<string, unknown>
            | undefined;
          const rb = (b.details as Record<string, unknown> | undefined)?.raw as
            | Record<string, unknown>
            | undefined;
          const sa = parseHHMM(ra?.start_time);
          const sb = parseHHMM(rb?.start_time);
          if (sa != null && sb != null) return sa - sb;
          if (sa != null) return -1;
          if (sb != null) return 1;
          return (a.created_at ?? "") < (b.created_at ?? "") ? -1 : 1;
        }),
    [entries, id, date],
  );

  const reviewQuery = useQuery({
    queryKey: ["today-task-review", id, date],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("today_task_reviews")
        .select("*")
        .eq("employee_id", id)
        .eq("review_date", date)
        .maybeSingle();
      if (error) throw error;
      return (data as Review | null) ?? null;
    },
  });

  // load names of remark/checked-by users
  const review = reviewQuery.data;
  const peopleIds = [review?.remark_by, review?.checked_by].filter(Boolean) as string[];
  const peopleNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const pid of peopleIds) {
      const p = profiles.find((x) => x.id === pid);
      if (p) m.set(pid, p.full_name || p.email);
    }
    return m;
  }, [peopleIds.join(","), profiles]);

  const [remarkDraft, setRemarkDraft] = useState("");
  useEffect(() => {
    setRemarkDraft(review?.remark ?? "");
  }, [review?.id, review?.remark]);

  const saveRemark = useMutation({
    mutationFn: async (text: string) => {
      const uid = profile?.id;
      if (!uid) throw new Error("not signed in");
      const { error } = await supabase
        .from("today_task_reviews")
        .upsert(
          {
            employee_id: id,
            review_date: date,
            remark: text,
            remark_by: uid,
            remark_at: new Date().toISOString(),
          },
          { onConflict: "employee_id,review_date" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Remark saved");
      qc.invalidateQueries({ queryKey: ["today-task-review", id, date] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  const markChecked = useMutation({
    mutationFn: async () => {
      const uid = profile?.id;
      if (!uid) throw new Error("not signed in");
      const { error } = await supabase
        .from("today_task_reviews")
        .upsert(
          {
            employee_id: id,
            review_date: date,
            checked_by: uid,
            checked_at: new Date().toISOString(),
          },
          { onConflict: "employee_id,review_date" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Marked as checked");
      qc.invalidateQueries({ queryKey: ["today-task-review", id, date] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  // ---- access guard (after hooks) ----
  if (loading) return null;
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const isHr = profile?.department === "hr";
  const isMod = role === "moderator";
  const canSee =
    role === "admin" ||
    isHr ||
    (isMod &&
      (modCanWorkspace(perms, "view_overview") ||
        modCanWorkspace(perms, "view_activity_feed") ||
        modCanWorkspace(perms, "search_users")));
  if (!canSee) {
    return (
      <>
        <AppHeader subtitle="Today's Task Summary" />
        <div className="px-[22px] pb-6">
          <div className="rounded-lg border border-border bg-card p-6 text-[13px] text-muted-foreground">
            Access restricted to Admin, Moderator, and HR.
          </div>
        </div>
      </>
    );
  }

  if (!employee) {
    return (
      <>
        <AppHeader subtitle="Today's Task Summary" />
        <div className="px-[22px] pb-6">
          <div className="rounded-lg border border-border bg-card p-6 text-[13px] text-muted-foreground">
            Employee not found.
          </div>
        </div>
      </>
    );
  }

  const isUploadDept = NO_HOURS_DEPTS.includes(employee.department);

  // stats
  let firstMin: number | null = null;
  let lastMin: number | null = null;
  let totalMins = 0;
  let firstUploadAt: string | null = null;
  let lastUploadAt: string | null = null;
  const counts = { completed: 0, in_progress: 0, pending: 0 };
  for (const e of dayEntries) {
    const raw = (e.details as Record<string, unknown> | undefined)?.raw as
      | Record<string, unknown>
      | undefined;
    const s = parseHHMM(raw?.start_time);
    const en = parseHHMM(raw?.end_time);
    if (s != null) firstMin = firstMin == null ? s : Math.min(firstMin, s);
    if (en != null) lastMin = lastMin == null ? en : Math.max(lastMin, en);
    if (s != null && en != null && en > s) totalMins += en - s;
    if (e.created_at) {
      if (!firstUploadAt || e.created_at < firstUploadAt) firstUploadAt = e.created_at;
      if (!lastUploadAt || e.created_at > lastUploadAt) lastUploadAt = e.created_at;
    }
    const cs = classifyEntryStatus(e);
    if (cs !== "no_update") counts[cs]++;
  }
  const total = dayEntries.length;
  const noUpdateCount = total === 0 ? 1 : 0; // visual only
  const overallStatus: UiStatus =
    total === 0 ? "no_update" : counts.in_progress > 0 ? "in_progress" : counts.pending > 0 ? "pending" : "completed";
  const pill = STATUS_PILL[overallStatus];

  // breakdown
  const pct = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));

  // notes
  const notes = dayEntries
    .map((e) => {
      const det = (e.details ?? {}) as Record<string, unknown>;
      const t = String(det.task_type ?? e.title ?? "");
      const note = String(det.summary ?? "").trim();
      return note ? { t, note } : null;
    })
    .filter(Boolean) as { t: string; note: string }[];

  // links
  const links = dayEntries
    .map((e) => {
      const det = (e.details ?? {}) as Record<string, unknown>;
      const url = det.link as string | undefined;
      if (!url) return null;
      const t = String(det.task_type ?? e.title ?? "Link");
      return { url: String(url), label: linkLabelFor(t, String(det.source_table ?? "")) };
    })
    .filter(Boolean) as { url: string; label: string }[];

  const exportReport = () => {
    const rows: string[] = [];
    rows.push(["#", "Task", "Description", "Start", "End", "Duration", "Status", "Link"].join(","));
    dayEntries.forEach((e, i) => {
      const det = (e.details ?? {}) as Record<string, unknown>;
      const raw = (det.raw ?? {}) as Record<string, unknown>;
      const start = fmt12(raw.start_time) ?? "";
      const end = fmt12(raw.end_time) ?? "";
      const sMin = parseHHMM(raw.start_time);
      const eMin = parseHHMM(raw.end_time);
      const dur = sMin != null && eMin != null && eMin > sMin ? fmtHM(eMin - sMin) : "";
      const status = STATUS_PILL[classifyEntryStatus(e)].label;
      const csv = [
        i + 1,
        String(det.task_type ?? "").replace(/"/g, '""'),
        String(det.summary ?? e.title ?? "").replace(/"/g, '""'),
        start,
        end,
        dur,
        status,
        String(det.link ?? ""),
      ]
        .map((v) => `"${v}"`)
        .join(",");
      rows.push(csv);
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `today-tasks-${employee.full_name || employee.email}-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const initial = (employee.full_name || employee.email).charAt(0).toUpperCase();

  return (
    <>
      <AppHeader subtitle="Today's Task Summary" />
      <div className="px-[22px] pb-10">
        {/* Header bar */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/today-tasks"
            className="inline-flex items-center gap-1.5 rounded-md border border-[#E5E7EB] bg-white px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-[#F5F3FF] hover:text-[#7C3AED]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Today's Task
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportReport}
              className="h-9 gap-1.5 text-[12px]"
            >
              <Download className="h-3.5 w-3.5" />
              Export Report
            </Button>
            <Button
              onClick={() => markChecked.mutate()}
              disabled={markChecked.isPending}
              className="h-9 gap-1.5 text-[12px] font-semibold text-white hover:opacity-90"
              style={{ background: "#7C3AED" }}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {review?.checked_at ? "Re-check" : "Mark Checked"}
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <h1 className="text-[22px] font-bold leading-tight">Today's Task Summary</h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Daily work review · {fmtDateLong(date)}
          </p>
        </div>

        {/* Employee summary card */}
        <div className="mb-4 rounded-[12px] border border-[#E5E7EB] bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start gap-4">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full text-[16px] font-bold text-white"
              style={{ background: "var(--gradient-avatar)" }}
            >
              {employee.avatar_url ? (
                <img src={employee.avatar_url} alt="" className="h-full w-full object-cover" />
              ) : (
                initial
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[16px] font-bold leading-tight">
                {employee.full_name || employee.email}
              </div>
              <div className="mt-0.5 text-[12px] text-muted-foreground">
                {DEPARTMENT_LABELS[employee.department]}
                {ROLE_LABELS[employee.department]
                  ? ` · ${ROLE_LABELS[employee.department]}`
                  : ""}
              </div>
              {review?.checked_at && (
                <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-[#F0FDF4] px-2 py-0.5 text-[10px] font-semibold text-[#15803D]">
                  <CheckCircle2 className="h-3 w-3" />
                  Checked by {peopleNames.get(review.checked_by ?? "") ?? "Reviewer"} ·{" "}
                  {fmtTs(review.checked_at)}
                </div>
              )}
            </div>
            <span
              className="rounded-full px-3 py-1 text-[11px] font-semibold"
              style={{ background: pill.bg, color: pill.fg }}
            >
              {pill.label}
            </span>
          </div>

          {/* Stat grid */}
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {isUploadDept ? (
              <>
                <Stat label="First Upload Time" value={fmtTs(firstUploadAt) || "—"} />
                <Stat label="Last Upload Time" value={fmtTs(lastUploadAt) || "—"} />
                <Stat label="Total Uploads" value={String(total)} />
                <Stat label="Completed Uploads" value={String(counts.completed)} />
                <Stat label="Task Count" value={String(total)} />
              </>
            ) : (
              <>
                <Stat
                  label="First Task Time"
                  value={firstMin != null ? formatMin(firstMin) : "—"}
                />
                <Stat
                  label="Last Task Time"
                  value={lastMin != null ? formatMin(lastMin) : "—"}
                />
                <Stat
                  label="Total Active Duration"
                  value={totalMins > 0 ? fmtHM(totalMins) : "—"}
                />
                <Stat label="Completed Tasks" value={String(counts.completed)} />
                <Stat label="Task Count" value={String(total)} />
              </>
            )}
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
          {/* Timeline */}
          <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[14px] font-bold">Today's Timeline</h2>
              <span className="text-[11px] text-muted-foreground">
                {total} {total === 1 ? "task" : "tasks"}
              </span>
            </div>

            {isLoading ? (
              <div className="text-[12px] text-muted-foreground">Loading…</div>
            ) : dayEntries.length === 0 ? (
              <div className="rounded-md border border-dashed border-[#E5E7EB] bg-[#FAFAFB] py-10 text-center text-[12px] text-muted-foreground">
                No tasks recorded on this date.
              </div>
            ) : (
              <ol className="relative space-y-3 border-l-2 border-[#EDE9FE] pl-5">
                {dayEntries.map((e, i) => {
                  const det = (e.details ?? {}) as Record<string, unknown>;
                  const raw = (det.raw ?? {}) as Record<string, unknown>;
                  const start = fmt12(raw.start_time);
                  const sMin = parseHHMM(raw.start_time);
                  const eMin = parseHHMM(raw.end_time);
                  const dur = sMin != null && eMin != null && eMin > sMin ? eMin - sMin : 0;
                  const st = classifyEntryStatus(e);
                  const sp = STATUS_PILL[st];
                  const taskType = String(det.task_type ?? e.title ?? "—");
                  const desc = String(det.summary ?? "").trim();
                  const left = isUploadDept
                    ? `Upload ${i + 1}`
                    : start ?? `#${i + 1}`;
                  return (
                    <li key={e.id} className="relative">
                      <span
                        className="absolute -left-[26px] top-1.5 h-3 w-3 rounded-full border-2 border-white"
                        style={{ background: sp.fg }}
                      />
                      <div className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-[#E5E7EB] bg-white px-3 py-2 hover:bg-[#FAFAFB]">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-[#7C3AED]">
                              {left}
                            </span>
                            <span className="text-[13px] font-semibold text-foreground">
                              {e.title || taskType}
                            </span>
                            {!isUploadDept && dur > 0 && (
                              <span className="text-[11px] text-muted-foreground">
                                · {fmtHM(dur)}
                              </span>
                            )}
                          </div>
                          {desc && (
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                              {desc}
                            </div>
                          )}
                        </div>
                        <span
                          className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          style={{ background: sp.bg, color: sp.fg }}
                        >
                          {sp.label}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* Right side panels */}
          <div className="space-y-4">
            {/* Task Breakdown */}
            <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-[13px] font-bold">Task Breakdown</h3>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-[#F3F4F6]">
                {counts.completed > 0 && (
                  <div
                    style={{ width: `${pct(counts.completed)}%`, background: "#16A34A" }}
                  />
                )}
                {counts.in_progress > 0 && (
                  <div
                    style={{ width: `${pct(counts.in_progress)}%`, background: "#7C3AED" }}
                  />
                )}
                {counts.pending > 0 && (
                  <div
                    style={{ width: `${pct(counts.pending)}%`, background: "#F59E0B" }}
                  />
                )}
                {noUpdateCount > 0 && (
                  <div style={{ width: `100%`, background: "#9CA3AF" }} />
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-[11px]">
                <BreakdownRow color="#16A34A" label="Completed" value={counts.completed} />
                <BreakdownRow color="#7C3AED" label="In Progress" value={counts.in_progress} />
                <BreakdownRow color="#F59E0B" label="Pending" value={counts.pending} />
                {total === 0 && (
                  <BreakdownRow color="#9CA3AF" label="No Update" value={1} />
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-[13px] font-bold">Notes</h3>
              {notes.length === 0 ? (
                <div className="text-[12px] text-muted-foreground">
                  No notes added today.
                </div>
              ) : (
                <ul className="space-y-2 text-[12px]">
                  {notes.map((n, i) => (
                    <li key={i} className="rounded-md bg-[#F9FAFB] px-2.5 py-1.5">
                      <div className="text-[10px] font-semibold uppercase text-[#7C3AED]">
                        {n.t}
                      </div>
                      <div className="mt-0.5 text-foreground">{n.note}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Files & Links */}
            <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-[13px] font-bold">Files & Links</h3>
              {links.length === 0 ? (
                <div className="text-[12px] text-muted-foreground">
                  No files or links shared today.
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {links.map((l, i) => (
                    <li key={i}>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-[12px] font-medium text-[#7C3AED] hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {l.label}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* HR Remarks */}
            <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm">
              <h3 className="mb-2 text-[13px] font-bold">HR Remarks</h3>
              <Textarea
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
                placeholder="Write internal remark for this employee's work today…"
                className="min-h-[80px] text-[12px]"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  onClick={() => saveRemark.mutate(remarkDraft)}
                  disabled={saveRemark.isPending}
                  className="h-8 text-[12px] font-semibold text-white hover:opacity-90"
                  style={{ background: "#7C3AED" }}
                >
                  Save Remark
                </Button>
              </div>
              {review?.remark && (
                <div className="mt-3 rounded-md border border-[#E5E7EB] bg-[#FAFAFB] p-2.5">
                  <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                    {peopleNames.get(review.remark_by ?? "") ?? "Reviewer"} ·{" "}
                    {fmtTs(review.remark_at)}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-[12px] text-foreground">
                    {review.remark}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-[#FAFAFB] px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-[14px] font-bold text-foreground">{value}</div>
    </div>
  );
}

function BreakdownRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="h-2 w-2 rounded-full" style={{ background: color }} />
        {label}
      </span>
      <span className="font-semibold text-foreground">{value}</span>
    </div>
  );
}

function formatMin(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12).padStart(2, "0")}:${String(min).padStart(2, "0")} ${ampm}`;
}

function linkLabelFor(taskType: string, sourceTable: string): string {
  const t = taskType.toLowerCase();
  if (t.includes("script")) return "Script Doc";
  if (t.includes("blog")) return "Blog Doc";
  if (t.includes("calendar")) return "Calendar Sheet";
  if (t.includes("campaign") || t.includes("ad")) return "Campaign Link";
  if (t.includes("research") || t.includes("competitor")) return "Report Doc";
  if (t.includes("brief")) return "Brief Doc";
  if (sourceTable.includes("designer") || sourceTable.includes("editor")) return "Drive Link";
  return "Open Link";
}
